import { amICoachingClientOptions } from "@/actions/client-metrics/handler";
import {
  getProgramDayOptions,
  getProgramOptions,
} from "@/actions/program/handler";
import {
  assignProgramFn,
  getActiveUserProgramOptions,
  updateProgramWeekFn,
} from "@/actions/user-program/handler";
import {
  getEffectiveMaxWeightsOptions,
  getLogsForDayOptions,
} from "@/actions/workout/handler";
import { useWorkoutLogMutation } from "@/actions/workout/useWorkoutLogMutation";
import {
  ProgramPresentationType,
  formatDaysPerWeek,
} from "@/api/db/schema/training-programs";
import { authClient } from "@/auth/auth-client";
import { JoinWithCodeDialog } from "@/components/program/JoinWithCodeDialog";
import { ProgramActivationWizard } from "@/components/program/ProgramActivationWizard";
import { ProgramSettingsDialog } from "@/components/program/ProgramSettingsDialog";
import { PurchasePreviewDialog } from "@/components/program/PurchasePreviewDialog";
import {
  CompactWorkoutSummary,
  type ExerciseRowData,
  type SetLogData,
  SmartTrainingTable,
  WorkoutSummary,
} from "@/components/training";
import { WorkoutMealSection } from "@/components/training/WorkoutMealCard";
import { WorkoutPosingSection } from "@/components/training/WorkoutPosingSection";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { calculateSmartWeight } from "@/utils/rpe-calculator";
import { seo } from "@/utils/seo";
import {
  getYouTubeEmbedUrl,
  isValidUrl,
  isYouTubeUrl,
  normalizeUrl,
} from "@/utils/video";
import { kgToUnit, unitToKg, useWeightUnit } from "@/utils/weight-unit";
import * as Sentry from "@sentry/tanstackstart-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Dumbbell,
  FileText,
  Flame,
  Play,
  ShieldCheck,
  ShoppingBag,
  Video,
} from "lucide-react";
import * as React from "react";
import { Suspense, useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

const programDetailSearchSchema = z.object({
  payment: z
    .enum(["success", "failed"])
    .catch(undefined as any)
    .optional(),
  error: z.string().optional(),
  view: z.enum(["showcase", "workout"]).optional(),
});

export const Route = createFileRoute("/_app/program/$programId")({
  validateSearch: (search) => programDetailSearchSchema.parse(search),
  loader: async ({ context, params }) => {
    const [data, , coachingResult] = await Promise.allSettled([
      context.queryClient.ensureQueryData(getProgramOptions(params.programId)),
      context.queryClient.ensureQueryData(getActiveUserProgramOptions()),
      context.queryClient.ensureQueryData(amICoachingClientOptions()),
    ]);

    if (coachingResult.status === "rejected") {
      Sentry.withScope((scope) => {
        scope.setTag("page", "/program/$programId");
        scope.setTag("loader_step", "amICoachingClient");
        scope.setExtra("loaderError", coachingResult.reason);
        Sentry.captureException(
          coachingResult.reason instanceof Error
            ? coachingResult.reason
            : new Error(
                `amICoachingClient loader hatasi: ${String(coachingResult.reason)}`,
              ),
        );
      });
    }

    const programData = data.status === "fulfilled" ? data.value : undefined;

    const coachingBody =
      coachingResult.status === "fulfilled"
        ? (
            coachingResult.value as
              | {
                  body?: {
                    isCoachingClient?: boolean;
                    assignment?: { hasPosingRoutine?: boolean };
                  };
                }
              | undefined
          )?.body
        : undefined;

    const isCoachingClient = coachingBody?.isCoachingClient ?? false;
    const hasPosingRoutine = Boolean(
      coachingBody?.assignment?.hasPosingRoutine,
    );

    return {
      program: programData,
      isCoachingClient,
      hasPosingRoutine,
    };
  },
  head: ({ loaderData }) => {
    const programData = loaderData?.program;
    const program =
      programData && "body" in programData ? programData.body : null;
    if (!program || "message" in program) return {};

    const effectiveName =
      ("catalogName" in program && program.catalogName) || program.name;
    const effectiveDescription =
      ("catalogDescription" in program && program.catalogDescription) ||
      program.description;

    return {
      meta: seo({
        title: `${effectiveName} | Antrenman Programı | AtalayTheCoach`,
        description:
          effectiveDescription ||
          `${effectiveName} antrenman programı ile gücünüzü artırın. ${program.durationWeeks} hafta, haftada ${program.daysPerWeek} gün antrenman.`,
        image: program.image || undefined,
        canonical: `https://atalaythecoach.com/program/${program.programId}`,
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            name: effectiveName,
            description: effectiveDescription,
            provider: {
              "@type": "Organization",
              name: "AtalayTheCoach",
              sameAs: "https://atalaythecoach.com",
            },
          }),
        },
      ],
    };
  },
  component: ProgramDetailPage,
});

function ProgramDetailPage() {
  const { programId } = Route.useParams();
  const search = Route.useSearch();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (search.payment === "success") {
      toast.success("Ödeme Başarılı!", {
        description:
          "Programınız aktif edildi. Hemen antrenmanınıza başlayabilirsiniz! 🎉",
      });
      queryClient.invalidateQueries({
        queryKey: getActiveUserProgramOptions().queryKey,
      });
    } else if (search.payment === "failed") {
      toast.error("Ödeme Başarısız", {
        description:
          "Ödeme işlemi tamamlanamadı. WhatsApp üzerinden destek alabilirsiniz.",
      });
    }
  }, [search.payment, queryClient]);

  return (
    <div className="container mx-auto max-w-5xl px-4 pt-24 pb-8">
      {/* Top Navigation & Settings */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <Link
          to="/program"
          className="inline-flex items-center gap-2 text-muted-foreground text-sm transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Programlara Dön
        </Link>

        <ProgramSettingsDialog />
      </div>

      <Suspense fallback={<ProgramDetailSkeleton />}>
        <ProgramContent programId={programId} />
      </Suspense>
    </div>
  );
}

interface ProgramContentProps {
  programId: string;
}

function ProgramContent({ programId }: ProgramContentProps) {
  const { user } = Route.useRouteContext();
  const search = Route.useSearch();
  const { isCoachingClient, hasPosingRoutine } = Route.useLoaderData();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: session } = authClient.useSession();
  const { unit } = useWeightUnit();

  // Fetch program data
  const { data: programData, isLoading: isProgramLoading } = useQuery(
    getProgramOptions(programId),
  );

  // Fetch user's active program
  const { data: activeProgramData, isLoading: isActiveProgramLoading } =
    useQuery(getActiveUserProgramOptions());

  const activeProgram = activeProgramData?.body;
  const isMyActiveProgram =
    activeProgram &&
    !("message" in activeProgram) &&
    "programId" in activeProgram
      ? activeProgram.programId === programId
      : false;

  const program = programData?.body;

  const isAdmin = Boolean(
    (program && "isAdmin" in program && program.isAdmin) ||
      session?.user?.role === "admin" ||
      user?.role === "admin",
  );

  const [adminViewMode, setAdminViewMode] = useState<"showcase" | "workout">(
    "showcase",
  );

  React.useEffect(() => {
    if (search.view) {
      setAdminViewMode(search.view);
    } else if (
      program &&
      "hasAccess" in program &&
      program.hasAccess === false
    ) {
      setAdminViewMode("showcase");
    } else if (
      program &&
      "hasAccess" in program &&
      program.hasAccess === true
    ) {
      setAdminViewMode("workout");
    }
  }, [search.view, program]);

  // Check if we have a valid program (not an error response)
  const isValidProgram = program && "days" in program;
  const days = isValidProgram ? program.days : [];

  // Week and day selection - start from currentWeek if this is user's active program
  const [workoutDate, setWorkoutDate] = useState(() => {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/Istanbul",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());
    return ["year", "month", "day"]
      .map((type) => parts.find((part) => part.type === type)?.value)
      .join("-");
  });
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  // Sync selected week when active program data is available
  const hasSyncedInitialWeek = useRef(false);
  if (
    isMyActiveProgram &&
    activeProgram &&
    !("message" in activeProgram) &&
    !hasSyncedInitialWeek.current
  ) {
    setSelectedWeek(activeProgram.currentWeek);
    setSelectedDayIndex(activeProgram.currentDayIndex || 0); // Initialize current day
    hasSyncedInitialWeek.current = true;
  }

  // Update week/day mutation
  const updatePositionMutation = useMutation({
    mutationFn: updateProgramWeekFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getActiveUserProgramOptions().queryKey,
      });
    },
  });

  const assignMutation = useMutation({
    mutationFn: assignProgramFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getActiveUserProgramOptions().queryKey,
      });
    },
  });

  const handleAssign = () => {
    if (!program || "message" in program) return;
    if (!session?.user && !user) {
      toast.info("Programı başlatmak için lütfen giriş yapın.");
      void navigate({
        to: "/password/login",
        search: { redirect: `/program/${programId}` },
      });
      return;
    }
    assignMutation.mutate({
      data: {
        programId: program.programId,
      },
    });
  };

  // Handle week change - update backend if this is user's active program
  const handleWeekChange = (newWeek: number) => {
    setSelectedWeek(newWeek);
    setSelectedDayIndex(0); // Reset to first day

    // Update backend for active program
    if (isMyActiveProgram && activeProgram && !("message" in activeProgram)) {
      updatePositionMutation.mutate({
        data: {
          userProgramId: activeProgram.userProgramId,
          weekNumber: newWeek,
          currentDayIndex: 0,
        },
      });
    }
  };

  // Handle day change - update backend
  const handleDayChange = (newDayIndex: number) => {
    setSelectedDayIndex(newDayIndex);

    if (isMyActiveProgram && activeProgram && !("message" in activeProgram)) {
      updatePositionMutation.mutate({
        data: {
          userProgramId: activeProgram.userProgramId,
          weekNumber: selectedWeek,
          currentDayIndex: newDayIndex,
        },
      });
    }
  };

  // Filter days for current week
  const weekDays = useMemo(
    () =>
      days.filter((d: { weekNumber: number }) => d.weekNumber === selectedWeek),
    [days, selectedWeek],
  );

  const selectedDay = weekDays[selectedDayIndex] ?? null;

  // Fetch day details with calculated weights
  const { data: dayData, isLoading: isDayLoading } = useQuery({
    ...getProgramDayOptions(selectedDay?.dayId ?? ""),
    enabled: !!selectedDay?.dayId,
  });

  // Extract exercise IDs from day data
  const exerciseIds = useMemo(() => {
    const dayBody = dayData?.body;
    if (!dayBody || "message" in dayBody) return [];
    return [...new Set(dayBody.exercises.map((e) => e.exerciseId))];
  }, [dayData]);

  // Fetch effective max weights (manual or RPE-estimated)
  const { data: effectiveMaxWeightsData } = useQuery(
    getEffectiveMaxWeightsOptions(exerciseIds),
  );

  // Optimistic mutation hook for workout log updates
  const {
    mutate: logUpdate,
    mutateAsync: logUpdateAsync,
    isPending,
  } = useWorkoutLogMutation();

  // Fetch logs for selected day
  // isPending sırasında refetchInterval'ı durdur — optimistic update'leri korur
  const { data: logsData } = useQuery({
    ...getLogsForDayOptions(programId, selectedDay?.dayId ?? ""),
    enabled: !!selectedDay?.dayId,
    refetchInterval: isPending
      ? false
      : (query) => {
          const logs = query.state.data?.body?.logs ?? [];
          const hasProcessing = logs.some(
            (l) =>
              l.videoProcessingStatus === "pending" ||
              l.videoProcessingStatus === "processing",
          );
          return hasProcessing ? 4000 : false;
        },
  });

  // Handle log update from table
  const handleLogUpdate = useCallback(
    (
      exerciseId: string,
      exerciseOrder: number,
      setNumber: number,
      data: Partial<SetLogData>,
    ) => {
      if (!selectedDay) return;

      const payload = { ...data };
      if (payload.completedWeight !== undefined) {
        payload.completedWeight = unitToKg(payload.completedWeight, unit);
      }

      logUpdate({
        programId,
        dayId: selectedDay.dayId,
        exerciseId,
        exerciseOrder,
        setNumber,
        ...payload,
      });
    },
    [logUpdate, programId, selectedDay, unit],
  );

  // Video yükleme için async versiyon — logId döndürür
  const handleLogUpdateAsync = useCallback(
    async (
      exerciseId: string,
      exerciseOrder: number,
      setNumber: number,
      data: Partial<SetLogData>,
    ) => {
      if (!selectedDay) return null;

      const payload = { ...data };
      if (payload.completedWeight !== undefined) {
        payload.completedWeight = unitToKg(payload.completedWeight, unit);
      }

      const result = await logUpdateAsync({
        programId,
        dayId: selectedDay.dayId,
        exerciseId,
        exerciseOrder,
        setNumber,
        ...payload,
      });

      // logId'yi döndür (trim job için gerekli)
      if (result?.status === 200 && result.body && "log" in result.body) {
        return (result.body as { log: { logId: string } }).log?.logId ?? null;
      }
      return null;
    },
    [logUpdateAsync, programId, selectedDay, unit],
  );

  // Transform data for table
  const tableData: ExerciseRowData[] = useMemo(() => {
    const dayBody = dayData?.body;
    if (!dayBody || "message" in dayBody) return [];

    // Use logs from query data
    const logs = logsData?.body?.logs ?? [];

    // Get effective max weights (manual or RPE-estimated)
    const maxWeights = effectiveMaxWeightsData?.body?.maxWeights || [];

    return dayBody.exercises.map((exercise) => {
      // Find logs for this exercise (with order for duplicates)
      const exerciseLogs = logs.filter(
        (l) =>
          l.exerciseId === exercise.exerciseId &&
          l.exerciseOrder === exercise.order,
      );

      // Create set logs
      const setLogs: SetLogData[] = [];
      const exerciseMax = maxWeights.find(
        (m) => m.exerciseId === exercise.exerciseId,
      );

      // Calculate target weight (in standard KG first)
      let targetWeightKg: number | null = null;
      const isEstimated = exerciseMax?.isEstimated ?? false;

      if (exerciseMax) {
        // Priority 1: Direct Percentage
        if (exercise.percentageOf1RM) {
          // Calculate based on percentage (e.g. 100kg * 0.8 = 80kg)
          // Round to nearest 2.5kg
          targetWeightKg =
            Math.round(
              (exerciseMax.maxWeight * (exercise.percentageOf1RM / 100)) / 2.5,
            ) * 2.5;
        }
        // Priority 2: RPE + Reps (Smart Calc)
        else if (exercise.reps && exercise.targetRPE) {
          targetWeightKg = calculateSmartWeight(
            exerciseMax.maxWeight,
            exercise.reps,
            exercise.targetRPE,
          );
        }
      }

      // Convert target weight to user's unit (round to 5 lbs step for LBS or 2.5 for KG)
      const targetWeight = kgToUnit(
        targetWeightKg,
        unit,
        unit === "lbs" ? 5 : 2.5,
      );

      for (let i = 0; i < exercise.sets; i++) {
        const log = exerciseLogs.find((l) => l.setNumber === i + 1);

        setLogs.push({
          logId: log?.logId ?? null,
          setNumber: i + 1,
          targetWeight,
          targetReps: exercise.reps,
          targetRPE: exercise.targetRPE ?? null,
          completedWeight: kgToUnit(log?.completedWeight, unit),
          completedReps: log?.completedReps ?? null,
          completedRPE: log?.completedRPE ?? null,
          notes: log?.notes ?? null,
          videoUrl: log?.videoUrl ?? null,
          videoThumbnailUrl: log?.videoThumbnailUrl ?? null,
          videoProcessingStatus: (log?.videoProcessingStatus ?? null) as
            | "pending"
            | "processing"
            | "done"
            | "error"
            | null,
          videoTrimStart: log?.videoTrimStart ?? null,
          videoTrimEnd: log?.videoTrimEnd ?? null,
        });
      }

      return {
        exercise: {
          ...exercise,
          calculatedTargetWeight: targetWeight,
          isTargetWeightEstimated: isEstimated,
          isAddedLoad: exerciseMax?.isAddedLoad ?? false,
        },
        setLogs,
        exerciseInfo: exercise.exerciseInfo,
      };
    });
  }, [dayData, logsData, effectiveMaxWeightsData, unit]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    let totalSets = 0;
    let completedSets = 0;
    let totalVolume = 0;
    let rpeSum = 0;
    let rpeCount = 0;

    for (const exerciseData of tableData) {
      for (const set of exerciseData.setLogs) {
        totalSets++;
        if (set.completedWeight !== null) {
          completedSets++;
          totalVolume += set.completedWeight * (set.completedReps ?? 1);
        }
        if (set.completedRPE !== null) {
          rpeSum += set.completedRPE;
          rpeCount++;
        }
      }
    }

    return {
      totalSets,
      completedSets,
      totalVolume: Math.round(totalVolume),
      averageRPE: rpeCount > 0 ? rpeSum / rpeCount : null,
    };
  }, [tableData]);

  if (isProgramLoading || isActiveProgramLoading) {
    return <ProgramDetailSkeleton />;
  }

  const adminSwitcherNode = isAdmin ? (
    <div className="flex flex-col items-start justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 p-3 text-xs sm:flex-row sm:items-center">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 font-bold text-foreground">
          <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
          <span>Yönetici Önizleme Modu:</span>
        </div>
        <div className="inline-flex rounded-lg border border-border/60 bg-background p-0.5 shadow-xs">
          <button
            type="button"
            onClick={() => setAdminViewMode("showcase")}
            className={cn(
              "cursor-pointer rounded-md px-3 py-1 font-semibold text-xs transition-all",
              adminViewMode === "showcase"
                ? "bg-primary text-primary-foreground shadow-xs"
                : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
            )}
          >
            Vitrin (Ziyaretçi)
          </button>
          <button
            type="button"
            onClick={() => setAdminViewMode("workout")}
            className={cn(
              "cursor-pointer rounded-md px-3 py-1 font-semibold text-xs transition-all",
              adminViewMode === "workout"
                ? "bg-primary text-primary-foreground shadow-xs"
                : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
            )}
          >
            Antrenman (Sporcu)
          </button>
        </div>
      </div>
      <Button
        asChild
        size="sm"
        variant="ghost"
        className="h-7 gap-1 font-medium text-xs hover:text-primary"
      >
        <Link to="/admin/program/$programId" params={{ programId }}>
          Admin Panelinde Düzenle &rarr;
        </Link>
      </Button>
    </div>
  ) : null;

  const hasAccess =
    program && "hasAccess" in program ? program.hasAccess : true;
  const isUnpurchased = hasAccess === false;
  const showShowcase = isAdmin ? adminViewMode === "showcase" : isUnpurchased;

  if (showShowcase && program) {
    return (
      <ProgramShowcase
        program={program as any}
        adminSwitcher={adminSwitcherNode}
      />
    );
  }

  if (!program || !isValidProgram) {
    const message =
      program && "message" in program
        ? program.message
        : "Program içeriğine şu anda erişilemiyor.";
    return (
      <Card>
        <CardContent className="flex flex-col items-center py-12 text-center">
          <h2 className="font-bold text-xl">Program erişimi gerekli</h2>
          <p className="mt-2 text-muted-foreground">{message}</p>
          <Button asChild variant="outline" className="mt-6">
            <Link to="/program">Programlara Dön</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const totalWeeks = program.durationWeeks;

  return (
    <div className="space-y-6">
      {adminSwitcherNode}

      {/* Program Banner Image if available */}
      {program.image && (
        <div className="flex w-full items-center justify-center overflow-hidden rounded-2xl border border-border/40 bg-muted/20 p-2">
          <img
            src={program.image}
            alt={program.name}
            className="h-auto max-h-56 w-full object-contain"
          />
        </div>
      )}

      {/* Program Header */}
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h1 className="font-bold text-2xl text-foreground">
                {program.name}
              </h1>
              {program.introVideoUrl && isValidUrl(program.introVideoUrl) && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <Play className="h-4 w-4" />
                      Tanıtımı İzle
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[95vh] w-[95vw] max-w-3xl overflow-hidden rounded-xl border bg-background p-0 sm:w-full">
                    <DialogHeader className="border-b px-4 py-4 sm:px-6">
                      <div className="flex flex-col gap-1 text-left sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                        <div className="min-w-0 flex-1">
                          <DialogTitle className="truncate font-bold text-foreground text-lg sm:text-xl">
                            Program Tanıtımı
                          </DialogTitle>
                          <p className="mt-0.5 font-semibold text-[10px] text-muted-foreground uppercase tracking-wide sm:text-xs">
                            {program.name}
                          </p>
                        </div>
                        <div className="hidden sm:block">
                          <div className="rounded-full bg-muted px-3 py-1 font-semibold text-[10px] text-muted-foreground uppercase tracking-wide">
                            Video Rehber
                          </div>
                        </div>
                      </div>
                    </DialogHeader>

                    <div className="relative aspect-video w-full bg-black shadow-inner">
                      {isYouTubeUrl(normalizeUrl(program.introVideoUrl)) ? (
                        <iframe
                          src={getYouTubeEmbedUrl(
                            normalizeUrl(program.introVideoUrl),
                          )}
                          title={`${program.name} tanıtım videosu`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="h-full w-full border-0"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center bg-muted/10 p-8 text-center">
                          <div className="max-w-sm space-y-4">
                            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                              <Video className="h-8 w-8 text-primary" />
                            </div>
                            <p className="font-medium text-muted-foreground text-sm">
                              Video oynatılamıyor. Lütfen harici bağlantıdan
                              izleyin.
                            </p>
                            <Button
                              asChild
                              variant="secondary"
                              size="sm"
                              className="rounded-xl font-bold"
                            >
                              <a
                                href={normalizeUrl(program.introVideoUrl)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2"
                              >
                                Videoyu Aç <ChevronRight className="h-4 w-4" />
                              </a>
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            {program.description && (
              <p className="mt-1 text-muted-foreground text-sm">
                {program.description}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="pt-2">
        {/* Intro Video removed from main flow - now in Dialog above */}
      </div>

      {!isMyActiveProgram && (
        <Card className="border-border/40 bg-secondary/20 shadow-none sm:rounded-3xl">
          <CardContent className="flex flex-col items-center gap-4 py-6 text-center sm:flex-row sm:justify-between sm:px-8 sm:py-8 sm:text-left">
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                <h3 className="font-bold text-foreground text-xl tracking-tight sm:text-2xl">
                  {activeProgramData?.body &&
                  !("message" in activeProgramData.body) &&
                  activeProgramData.body.programId !== program.programId
                    ? "Başka Bir Program Aktif"
                    : "Bu Program Aktif Değil"}
                </h3>
              </div>

              <p className="max-w-md text-muted-foreground text-sm leading-relaxed">
                {activeProgramData?.body &&
                !("message" in activeProgramData.body) &&
                activeProgramData.body.programId !== program.programId
                  ? `Şu an "${activeProgramData.body.program?.name}" programını uyguluyorsunuz. Bu programa geçerseniz mevcut ilerlemeniz duraklatılacaktır.`
                  : "Bu programı önizlemektesiniz. İlerlemeyi kaydetmek ve ağırlık takibi yapabilmek için programı başlatın."}
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2.5">
              <ProgramActivationWizard
                programName={program.name}
                isPending={assignMutation.isPending}
                onConfirm={handleAssign}
                hasActiveProgram={
                  !!activeProgramData?.body &&
                  !("message" in activeProgramData.body)
                }
              >
                <Button
                  size="lg"
                  disabled={assignMutation.isPending}
                  className="rounded-2xl font-bold shadow-md"
                >
                  {assignMutation.isPending
                    ? "Başlatılıyor..."
                    : activeProgramData?.body &&
                        !("message" in activeProgramData.body) &&
                        activeProgramData.body.programId !== program.programId
                      ? "Bu Programa Geç"
                      : "Bu Programı Başlat"}
                </Button>
              </ProgramActivationWizard>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Week Controls */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setSelectedWeek((w) => Math.max(1, w - 1))}
          disabled={selectedWeek === 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <Select
            value={selectedWeek.toString()}
            onValueChange={(v) => handleWeekChange(Number(v))}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: totalWeeks }, (_, i) => (
                <SelectItem key={i + 1} value={(i + 1).toString()}>
                  Hafta {i + 1}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="outline"
          size="icon"
          onClick={() => setSelectedWeek((w) => Math.min(totalWeeks, w + 1))}
          disabled={selectedWeek === totalWeeks}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {weekDays.length > 0 ? (
        <div className="no-scrollbar flex flex-nowrap gap-2 overflow-x-auto pb-2">
          {weekDays.map(
            (
              day: { dayId: string; dayNumber: number; dayName: string },
              index: number,
            ) => (
              <Button
                key={day.dayId}
                variant={selectedDayIndex === index ? "default" : "outline"}
                size="sm"
                onClick={() => handleDayChange(index)}
                className="shrink-0"
              >
                Gün {day.dayNumber}: {day.dayName}
              </Button>
            ),
          )}
        </div>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Bu hafta için henüz gün eklenmemiş.
          </CardContent>
        </Card>
      )}

      {/* Summary - Compact on mobile */}
      {selectedDay && (
        <>
          <div className="md:hidden">
            <CompactWorkoutSummary
              completedSets={summaryStats.completedSets}
              totalSets={summaryStats.totalSets}
            />
          </div>
          <div className="hidden md:block">
            <WorkoutSummary {...summaryStats} unit={unit} />
          </div>
        </>
      )}

      {selectedDay &&
        (
          [
            { section: "warmup", title: "Isınma & Mobilite", icon: Flame },
            {
              section: "workout",
              title: "Ana Antrenman & Çalışma",
              icon: Dumbbell,
            },
          ] as const
        ).map(({ section, title, icon: SectionIcon }) => {
          const exercises = tableData.filter(
            (row) => (row.exercise.section ?? "workout") === section,
          );
          if (exercises.length === 0 && !isDayLoading) return null;
          return (
            <section key={section} className="space-y-3">
              <div className="flex items-center gap-2">
                <SectionIcon className="h-5 w-5 text-primary" />
                <h2 className="font-semibold text-xl">{title}</h2>
              </div>
              <SmartTrainingTable
                exercises={exercises}
                onLogUpdate={handleLogUpdate}
                onLogUpdateAsync={handleLogUpdateAsync}
                isLoading={isDayLoading}
                userRole={user?.role}
                isCoachingClient={isCoachingClient}
                weekNumber={selectedWeek}
                disabled={!isMyActiveProgram}
                unit={unit}
              />
            </section>
          );
        })}

      {/* Workout Meal Tracking - Sadece Koçluk Alanlara Açık */}
      {selectedDay && isMyActiveProgram && isCoachingClient && (
        <WorkoutMealSection
          programId={programId}
          date={workoutDate}
          onDateChange={setWorkoutDate}
          isCoachingClient={isCoachingClient}
        />
      )}

      {/* Bodybuilding Poz Rutini - Sadece Koç Tarafından Atanan Sporculara Açık */}
      {selectedDay &&
        isMyActiveProgram &&
        isCoachingClient &&
        (hasPosingRoutine || isAdmin) && (
          <WorkoutPosingSection
            programId={programId}
            date={workoutDate}
            onDateChange={setWorkoutDate}
          />
        )}

      {/* Day Notes */}
      {selectedDay?.notes && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Notlar</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-muted-foreground text-sm">
              {selectedDay.notes}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function parseDescription(description?: string | null) {
  if (!description) return { intro: null, steps: [], raw: "" };

  const trimmed = description.trim();
  const lines = trimmed.split("\n");
  const introLines: string[] = [];
  const steps: { number: string; title: string; body: string }[] = [];
  let inSteps = false;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    if (
      line.toLowerCase().includes("süreç nasıl") ||
      line.toLowerCase().includes("nasıl işliyor") ||
      line.toLowerCase().includes("adımlar")
    ) {
      inSteps = true;
      continue;
    }

    const stepMatch = line.match(/^(\d+)[\.\)]\s*([^:]+)(?::\s*(.*))?$/);
    if (stepMatch) {
      inSteps = true;
      steps.push({
        number: stepMatch[1].padStart(2, "0"),
        title: stepMatch[2].trim(),
        body: stepMatch[3] ? stepMatch[3].trim() : "",
      });
    } else if (inSteps && steps.length > 0) {
      steps[steps.length - 1].body +=
        (steps[steps.length - 1].body ? " " : "") + line;
    } else {
      introLines.push(line);
    }
  }

  return {
    intro: introLines.join("\n\n"),
    steps,
    raw: trimmed,
  };
}

interface ProgramShowcaseProps {
  program: {
    programId: string;
    name: string;
    description: string | null;
    durationWeeks: number;
    daysPerWeek: number;
    maxDaysPerWeek?: number | null;
    difficulty: string | null;
    isPaid?: boolean | null;
    price?: number | null;
    oldPrice?: number | null;
    image?: string | null;
    introVideoUrl?: string | null;
    accessCode?: string | null;
    catalogItemId?: string;
    priceKurus?: number;
    catalogName?: string | null;
    catalogDescription?: string | null;
    catalogItemType?: string | null;
    catalogImageUrl?: string | null;
    catalogSlug?: string | null;
    presentationType?: string | null;
  };
  adminSwitcher?: React.ReactNode;
}

function ProgramShowcase({ program, adminSwitcher }: ProgramShowcaseProps) {
  const effectiveName = program.catalogName || program.name;
  const effectiveDescription =
    program.catalogDescription || program.description;
  const isPersonalizedPresentation =
    program.presentationType === ProgramPresentationType.PERSONALIZED;
  const parsed = useMemo(
    () => parseDescription(effectiveDescription),
    [effectiveDescription],
  );

  const displayPriceTl = program.priceKurus
    ? program.priceKurus / 100
    : (program.price ?? null);

  const priceFormatted = displayPriceTl
    ? `₺${displayPriceTl.toLocaleString("tr-TR")}`
    : "Fiyat için iletişime geçin";
  const oldPriceFormatted =
    !program.priceKurus &&
    displayPriceTl &&
    program.oldPrice &&
    program.oldPrice > displayPriceTl
      ? `₺${program.oldPrice.toLocaleString("tr-TR")}`
      : null;

  const whatsappMessage = encodeURIComponent(
    `Merhaba, "${effectiveName}" programı hakkında bilgi almak ve kaydımı tamamlamak istiyorum.`,
  );
  const whatsappUrl = `https://wa.me/905449642824?text=${whatsappMessage}`;

  return (
    <div className="mx-auto max-w-2xl space-y-5 pb-20 sm:pb-6">
      {/* Optional Admin Switcher */}
      {adminSwitcher}

      {/* Hero / Overview Card */}
      <div className="space-y-4 rounded-2xl border border-border/70 bg-card p-5 shadow-xs sm:p-6">
        {/* Cover Image if present */}
        {program.image && (
          <div className="flex items-center justify-center overflow-hidden rounded-xl border border-border/40 bg-muted/20 p-2">
            <img
              src={program.image}
              alt={effectiveName}
              className="max-h-48 w-full object-contain"
            />
          </div>
        )}

        {/* Title and Badges */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className="border-primary/30 bg-primary/10 font-bold text-primary text-xs"
            >
              {isPersonalizedPresentation
                ? "Kişiye Özel Hazırlanan Program"
                : "Hazır Antrenman Programı"}
            </Badge>
            {program.difficulty && (
              <Badge variant="secondary" className="font-medium text-xs">
                {program.difficulty}
              </Badge>
            )}
          </div>

          <h1 className="font-extrabold text-2xl text-foreground tracking-tight sm:text-3xl">
            {effectiveName}
          </h1>

          {/* Quick Metrics Bar */}
          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            <div className="flex items-center gap-1.5 rounded-lg border border-border/60 bg-muted/40 px-2.5 py-1 font-medium text-muted-foreground text-xs">
              <Calendar className="h-3.5 w-3.5 shrink-0 text-primary" />
              <span>{program.durationWeeks} Hafta</span>
            </div>
            <div className="flex items-center gap-1.5 rounded-lg border border-border/60 bg-muted/40 px-2.5 py-1 font-medium text-muted-foreground text-xs">
              <Clock className="h-3.5 w-3.5 shrink-0 text-primary" />
              <span>
                {formatDaysPerWeek(program.daysPerWeek, program.maxDaysPerWeek)}
              </span>
            </div>
            {program.introVideoUrl && isValidUrl(program.introVideoUrl) && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 rounded-lg font-medium text-xs"
                  >
                    <Play className="h-3 w-3 text-primary" />
                    Tanıtım Videosu
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[95vh] w-[95vw] max-w-2xl overflow-hidden rounded-xl border bg-background p-0 sm:w-full">
                  <DialogHeader className="border-b px-5 py-3">
                    <DialogTitle className="font-bold text-base">
                      {effectiveName} Tanıtımı
                    </DialogTitle>
                  </DialogHeader>
                  <div className="aspect-video w-full bg-black">
                    {isYouTubeUrl(normalizeUrl(program.introVideoUrl)) ? (
                      <iframe
                        src={getYouTubeEmbedUrl(
                          normalizeUrl(program.introVideoUrl),
                        )}
                        title={`${effectiveName} tanıtım videosu`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="h-full w-full border-0"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center p-6 text-center">
                        <Button asChild variant="secondary">
                          <a
                            href={normalizeUrl(program.introVideoUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Harici Bağlantıda İzle
                          </a>
                        </Button>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Intro text */}
        {parsed.intro ? (
          <p className="whitespace-pre-line border-border/40 border-t pt-3 text-muted-foreground text-sm leading-relaxed">
            {parsed.intro}
          </p>
        ) : parsed.raw && parsed.steps.length === 0 ? (
          <p className="whitespace-pre-line border-border/40 border-t pt-3 text-muted-foreground text-sm leading-relaxed">
            {parsed.raw}
          </p>
        ) : null}
      </div>

      {/* How it Works (Süreç) - Compact timeline */}
      {parsed.steps.length > 0 && (
        <div className="space-y-3.5 rounded-2xl border border-border/70 bg-card p-5 shadow-xs sm:p-6">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            <h2 className="font-bold text-base text-foreground tracking-tight">
              Süreç Nasıl İşliyor?
            </h2>
          </div>
          <div className="space-y-3">
            {parsed.steps.map((step) => (
              <div key={step.number} className="flex items-start gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-primary/10 font-bold text-primary text-xs">
                  {step.number}
                </span>
                <div className="space-y-0.5 text-xs sm:text-sm">
                  <h3 className="font-semibold text-foreground">
                    {step.title}
                  </h3>
                  {step.body && (
                    <p className="text-muted-foreground leading-relaxed">
                      {step.body}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Program Highlights - Compact list */}
      <div className="space-y-3 rounded-2xl border border-border/60 bg-muted/20 p-4 sm:p-5">
        <h3 className="font-semibold text-foreground text-xs uppercase tracking-wider">
          Program Kapsamı & Özellikleri
        </h3>
        <ul className="space-y-2 text-muted-foreground text-xs sm:text-sm">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
            <span>
              {isPersonalizedPresentation
                ? "Form yanıtlarına göre kişiselleştirilmiş antrenman planlaması ve haftalık periyotlama"
                : "Önceden planlanmış antrenman yapısı ve haftalık periyotlama"}
            </span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
            <span>
              RPE & 1RM tabanlı otomatik ağırlık ve hedef tekrar hesaplaması
            </span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
            <span>
              Her egzersiz için video form kılavuzları ve set kayıt günlüğü
            </span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
            <span>Mobil ve web üzerinden sınırsız & ömür boyu tam erişim</span>
          </li>
        </ul>
      </div>

      {/* Main In-Page Purchase Card */}
      <div className="space-y-4 rounded-2xl border border-border/80 bg-card p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-1 border-border/50 border-b pb-3 sm:flex-row sm:items-baseline sm:justify-between">
          <div>
            <span className="block font-semibold text-muted-foreground text-xs uppercase tracking-wider">
              Paket Fiyatı
            </span>
            <div className="mt-0.5 flex items-baseline gap-2">
              <span className="font-black text-2xl text-foreground tracking-tight sm:text-3xl">
                {priceFormatted}
              </span>
              {oldPriceFormatted && (
                <span className="font-bold text-muted-foreground/60 text-sm line-through">
                  {oldPriceFormatted}
                </span>
              )}
            </div>
          </div>
          <span className="text-muted-foreground text-xs">
            Tek seferlik ödeme • Ömür boyu erişim
          </span>
        </div>

        <div className="space-y-2.5">
          <PurchasePreviewDialog
            programId={program.programId}
            catalogItemId={program.catalogItemId}
            programName={effectiveName}
            priceKurus={
              program.priceKurus ??
              (program.price ? program.price * 100 : undefined)
            }
            durationWeeks={program.durationWeeks}
            daysPerWeek={program.daysPerWeek}
            maxDaysPerWeek={program.maxDaysPerWeek}
          >
            <Button
              size="lg"
              className="h-12 w-full cursor-pointer rounded-xl bg-primary font-bold text-primary-foreground text-sm shadow-sm hover:bg-primary/90"
            >
              <ShoppingBag className="mr-2 h-4 w-4" />
              Hemen Satın Al ve Başla
            </Button>
          </PurchasePreviewDialog>

          {program.accessCode && (
            <JoinWithCodeDialog>
              <Button
                variant="outline"
                size="sm"
                className="h-9 w-full rounded-xl font-semibold text-xs"
              >
                Erişim Kodum Var
              </Button>
            </JoinWithCodeDialog>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 pt-1 text-[11px] text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
            <span>İyzico 3D Secure Güvenli Ödeme</span>
          </div>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="underline transition-colors hover:text-foreground"
          >
            WhatsApp Destek
          </a>
        </div>
      </div>

      {/* Mobile Sticky Bottom Bar (Fixed at bottom on small screens) */}
      <div className="fixed right-0 bottom-0 left-0 z-40 flex items-center justify-between gap-3 border-border/80 border-t bg-background/95 px-4 py-3 shadow-lg backdrop-blur-md sm:hidden">
        <div className="min-w-0">
          <span className="block font-medium text-[10px] text-muted-foreground uppercase tracking-wider">
            Ömür Boyu Erişim
          </span>
          <span className="block truncate font-extrabold text-base text-foreground">
            {priceFormatted}
          </span>
        </div>
        <PurchasePreviewDialog
          programId={program.programId}
          catalogItemId={program.catalogItemId}
          programName={effectiveName}
          priceKurus={
            program.priceKurus ??
            (program.price ? program.price * 100 : undefined)
          }
          durationWeeks={program.durationWeeks}
          daysPerWeek={program.daysPerWeek}
          maxDaysPerWeek={program.maxDaysPerWeek}
        >
          <Button
            size="sm"
            className="h-10 shrink-0 rounded-xl bg-primary px-4 font-bold text-primary-foreground text-xs shadow-sm"
          >
            <ShoppingBag className="mr-1.5 h-3.5 w-3.5" />
            Satın Al ve Başla
          </Button>
        </PurchasePreviewDialog>
      </div>
    </div>
  );
}

function ProgramDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-4 w-full" />
      <div className="flex gap-4">
        <Skeleton className="h-10 w-10" />
        <Skeleton className="h-10 w-[140px]" />
        <Skeleton className="h-10 w-10" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-9 w-24" />
        <Skeleton className="h-9 w-24" />
        <Skeleton className="h-9 w-24" />
      </div>
      <Skeleton className="h-[300px] w-full" />
    </div>
  );
}
